import { useEffect, useState } from 'react';

import { InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ApplicationsGridHeader from 'components/ApplicationsGrid/components/ApplicationsGridHeader';
import WithArrow from 'components/WithArrow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useTranslation from './hooks/useTranslation';

import ApplicationsGridRow from './components/ApplicationsGridRow';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ApplicationsGrid: FunctionComponent<Props> = ({
    className = '',
    onNext,
    rows = [],
    testId = TEST_ID
}) => {
    const {
        LAST_ACTIVITY,
        LAST_EDITED,
        LOADING,
        NAME,
        NO_APPLICATIONS,
        NO_MORE_APPLICATIONS,
        STATUS
    } = useTranslation();

    const { containerRef, handleResize } = useGridTablesResizeObserver(
        '--applications-grid-row-columns'
    );
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = initialized && rows.length === 0 ? NO_APPLICATIONS : NO_MORE_APPLICATIONS;

    return (
        <VerticalContainer
            className={`${className} ${styles.applicationsGrid}`}
            ref={containerRef}
            testId={testId}>
            <ApplicationsGridHeader>
                <WithResize onResize={handleResize}>
                    <WithArrow className={styles.withArrow}>
                        <GridText text={NAME} />
                    </WithArrow>
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={STATUS} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={LAST_EDITED} />
                </WithResize>

                <GridText text={LAST_ACTIVITY} />
            </ApplicationsGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                loader={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={LOADING} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}>
                {rows.map(({ id, name, status, updatedAt }) => (
                    <ApplicationsGridRow
                        id={id}
                        key={id}
                        lastEdited={updatedAt}
                        name={name}
                        status={status}
                    />
                ))}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default ApplicationsGrid;
